import { events } from "@constants/tracking";
import useAnalytics from "@hooks/useAnalytics";
import { useGlobalStore } from "@hooks/useGlobalStore";
import { isQuestionTypeTag } from "@memorang/helpers/tag";
import type { BaseItemSchema } from "@memorang/types";
import { useSessionAutoStartAllowed } from "@memorang/ui/components/StudySession/SessionAutoStartContext";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
	type QuestionTipsTrigger,
	useQuestionTipsStore,
} from "../../hooks/useQuestionTipsStore";

type Args = {
	currentItem?: BaseItemSchema;
	isSummativeTest?: boolean;
	inReviewMode?: boolean;
	sessionId?: number;
};

const useQuestionTipsDialog = ({
	currentItem,
	isSummativeTest,
	inReviewMode,
	sessionId,
}: Args) => {
	const { t } = useTranslation();
	const { explicitCallTrackCustomEvent } = useAnalytics();
	const autoStartAllowed = useSessionAutoStartAllowed();

	const openedVia = useQuestionTipsStore((s) => s.openedVia);
	const openQuestionTips = useQuestionTipsStore((s) => s.openQuestionTips);
	const closeQuestionTips = useQuestionTipsStore((s) => s.closeQuestionTips);

	const shownQuestionTipTagIds = useGlobalStore(
		(s) => s.shownQuestionTipTagIds,
	);
	const setQuestionTipTagIdShown = useGlobalStore(
		(s) => s.setQuestionTipTagIdShown,
	);
	const hideFutureQuestionTips = useGlobalStore(
		(s) => s.hideFutureQuestionTips,
	);
	const setHideFutureQuestionTips = useGlobalStore(
		(s) => s.setHideFutureQuestionTips,
	);

	const aboutInfo =
		currentItem?.metadata?.secondary_metadata?.aboutInfo ??
		currentItem?.stimulusMetadata?.secondary_metadata?.aboutInfo;

	// The question type is a tag, not the item's own `type` — that is an
	// interaction type shared across many question types.
	const questionTypeTag = currentItem?.tags?.find(isQuestionTypeTag);
	const hasSeenQuestionType = questionTypeTag
		? Boolean(shownQuestionTipTagIds[String(questionTypeTag.id)])
		: false;

	const shouldAutoShow = Boolean(
		aboutInfo &&
			questionTypeTag &&
			!isSummativeTest &&
			!inReviewMode &&
			!hideFutureQuestionTips &&
			!hasSeenQuestionType &&
			autoStartAllowed,
	);

	const handleOpen = (trigger: QuestionTipsTrigger) => {
		openQuestionTips(trigger);
		explicitCallTrackCustomEvent({
			eventName: events.sessionQuestionTipsViewed,
			trigger,
			questionTypeTagId: questionTypeTag?.id,
			questionTypeTitle: questionTypeTag?.title,
			itemId: currentItem?.id,
			sessionId,
		});
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: opens once per unseen question type
	useEffect(() => {
		if (shouldAutoShow) {
			handleOpen("auto");
		}
	}, [shouldAutoShow]);

	const handleClose = () => {
		if (questionTypeTag) {
			setQuestionTipTagIdShown(questionTypeTag.id);
		}
		closeQuestionTips();
	};

	const handleToggleHideFuture = () => {
		const hide = !hideFutureQuestionTips;
		setHideFutureQuestionTips(hide);
		explicitCallTrackCustomEvent({
			eventName: events.sessionQuestionTipsHiddenToggled,
			hidden: String(hide),
			questionTypeTagId: questionTypeTag?.id,
			sessionId,
		});
	};

	return {
		aboutInfo,
		visible: openedVia !== undefined,
		handleClose,
		aboutConfig: aboutInfo
			? {
					onPress: () => handleOpen("manual"),
					label: t("session.navigation.about"),
				}
			: undefined,
		hideFutureConfig:
			openedVia === "auto"
				? {
						checked: Boolean(hideFutureQuestionTips),
						onToggle: handleToggleHideFuture,
						label: t("session.questionTips.hideFutureLabel"),
					}
				: undefined,
	};
};

export default useQuestionTipsDialog;
